<div class="policy-page-container">
  <div class="page-top-section">
    <a routerLink="/" class="back-to-home-link">← Back to Home</a>
    <img src="assets/images/logo/Dashboardly-logo.svg" alt="Dashboardly" class="centered-logo" />
  </div>

  <div class="policy-page-header">
    <div class="policy-page-title">
      <h1>Privacy Policy</h1>
      <p class="effective-date">Effective Date: {{ effectiveDate }}</p>
      <p class="effective-date">Last Updated: {{ lastUpdated }}</p>
    </div>
    <div class="policy-page-actions">
      <button class="btn-print" (click)="printPolicy()">
        <i class="fa fa-print"></i>
        Print
      </button>
    </div>
  </div>

  <div class="policy-page-content">
    <div class="policy-main-content">
      <p class="intro-text">
        DASHBOARDLY INC ("Dashboardly," "we," "our," or "us") provides SaaS software for profit analytics and inventory
        management tailored for TikTok Shop sellers, brands, and agencies. We are committed to protecting your
        information and maintaining transparency about how we collect, use, and safeguard data.
      </p>

      <p class="intro-text">
        This Privacy Policy applies to all users of our website (dashboardly.io), our web application, and our TikTok
        Shop integrations.
      </p>

      <div class="policy-section">
        <h2>1. Data We Collect</h2>

        <h3>From TikTok Shop (via official OAuth APIs)</h3>
        <p>When you connect your TikTok Shop account, we may access:</p>
        <ul>
          <li>
            <strong>Orders:</strong>
            IDs, SKUs, product details, timestamps, order value, discounts, refunds, shipping/handling fees.
          </li>
          <li>
            <strong>Products:</strong>
            SKUs, product IDs, names, pricing, inventory levels, listing metadata.
          </li>
          <li>
            <strong>Financials:</strong>
            TikTok commissions, transaction fees, taxes, promotions, payouts.
          </li>
          <li>
            <strong>Advertising:</strong>
            TikTok Ads spend & performance (if authorized).
          </li>
        </ul>

        <h3>From you directly</h3>
        <ul>
          <li>
            <strong>Account information:</strong>
            name, email, password (hashed with bcrypt/argon2).
          </li>
          <li>
            <strong>Business inputs:</strong>
            cost of goods (COGS), supplier info, inventory adjustments, purchase orders.
          </li>
          <li>
            <strong>Billing details:</strong>
            subscription plan, invoices, payment details (processed securely by Stripe; we do not store card numbers).
          </li>
          <li>
            <strong>Support communications:</strong>
            messages via email or (in future) chat.
          </li>
        </ul>

        <h3>Automatically collected</h3>
        <ul>
          <li>Device/browser type, IP, approximate geolocation.</li>
          <li>Session data, login timestamps, usage logs, crash/error reports.</li>
          <li>Analytics on feature usage for product improvement.</li>
        </ul>

        <h3>Cookies</h3>
        <ul>
          <li>
            <strong>Essential:</strong>
            authentication, session, security.
          </li>
          <li>
            <strong>Analytics:</strong>
            anonymized usage tracking (Google Analytics).
          </li>
          <li>No third-party advertising cookies inside our app.</li>
        </ul>
      </div>

      <div class="policy-section">
        <h2>2. Purposes of Processing</h2>
        <p>We process collected data to:</p>
        <ul>
          <li>Deliver dashboards, profit analytics, inventory tracking, and alerts.</li>
          <li>Provide user support via email (hello&#64;dashboardly.io) and (optionally) chat.</li>
          <li>Improve reliability, security, and features of the platform.</li>
          <li>Ensure compliance with TikTok Developer Terms, GDPR, CCPA, and other regulations.</li>
          <li>Communicate service updates and, with explicit consent, send marketing emails.</li>
        </ul>
      </div>

      <div class="policy-section">
        <h2>3. Legal Basis for Processing (GDPR)</h2>
        <ul>
          <li>
            <strong>Contract:</strong>
            to deliver the services you subscribed to.
          </li>
          <li>
            <strong>Legitimate interests:</strong>
            to improve services, prevent fraud, and ensure security.
          </li>
          <li>
            <strong>Consent:</strong>
            for optional marketing and non-essential cookies.
          </li>
          <li>
            <strong>Legal obligations:</strong>
            when required by law or regulator.
          </li>
        </ul>
      </div>

      <div class="policy-section">
        <h2>4. Data Sharing & Subprocessors</h2>
        <p>
          We do not sell personal data. We share information only with trusted subprocessors, each bound by Data
          Processing Agreements (DPAs):
        </p>
        <ul>
          <li>
            <strong>Hosting:</strong>
            DigitalOcean (secure data centers).
          </li>
          <li>
            <strong>Payments:</strong>
            Stripe (PCI-DSS certified).
          </li>
          <li>
            <strong>Analytics:</strong>
            Google Analytics (anonymized).
          </li>
          <li>
            <strong>Support:</strong>
            email (hello&#64;dashboardly.io); future chat (e.g., Intercom or Crisp).
          </li>
          <li>
            <strong>Monitoring & error tracking:</strong>
            Sentry (errors) and Datadog (infrastructure monitoring).
          </li>
        </ul>
      </div>

      <div class="policy-section">
        <h2>5. Security Measures</h2>
        <p>We follow industry best practices to protect your data:</p>
        <ul>
          <li>
            <strong>Encryption:</strong>
            TLS 1.2+ in transit; AES-256 at rest.
          </li>
          <li>
            <strong>Passwords:</strong>
            hashed and salted (bcrypt/argon2).
          </li>
          <li>
            <strong>Access control:</strong>
            Role-Based Access Control (RBAC), least-privilege model, audit logs.
          </li>
          <li>
            <strong>Monitoring:</strong>
            real-time monitoring, anomaly detection, incident response protocols.
          </li>
          <li>
            <strong>Backups:</strong>
            encrypted, rotated, retained only as necessary.
          </li>
          <li>
            <strong>Employee training:</strong>
            regular security & privacy training.
          </li>
          <li>
            <strong>Testing:</strong>
            penetration tests by independent security experts.
          </li>
        </ul>
      </div>

      <div class="policy-section">
        <h2>6. Data Retention & Deletion</h2>
        <ul>
          <li>
            <strong>Account data:</strong>
            retained while your account is active.
          </li>
          <li>
            <strong>TikTok data:</strong>
            deleted automatically within 30 days of app uninstall.
          </li>
          <li>
            <strong>Manual deletion requests:</strong>
            via hello&#64;dashboardly.io or Data Deletion Page.
          </li>
          <li>
            <strong>Backups:</strong>
            purged securely within 60 days of account termination.
          </li>
          <li>
            <strong>Billing records:</strong>
            retained up to 7 years (tax compliance).
          </li>
        </ul>
      </div>

      <div class="policy-section">
        <h2>7. Your Rights</h2>
        <p>Depending on your jurisdiction (GDPR, CCPA, PIPEDA, etc.), you may:</p>
        <ul>
          <li>
            <strong>Access</strong>
            your personal data.
          </li>
          <li>
            <strong>Rectify</strong>
            incorrect or incomplete data.
          </li>
          <li>
            <strong>Request erasure</strong>
            ("right to be forgotten").
          </li>
          <li>
            <strong>Restrict or object</strong>
            to certain processing.
          </li>
          <li>
            <strong>Port your data</strong>
            to another provider in machine-readable format.
          </li>
          <li>
            <strong>Opt out</strong>
            of marketing at any time.
          </li>
        </ul>
        <p>We respond to verified requests within 30 days. Under CCPA, you may appoint an authorized agent.</p>
      </div>

      <div class="policy-section">
        <h2>8. International Transfers</h2>
        <p>
          Your data may be processed in the United States, Canada, or the EU. For transfers outside the EEA, we rely on:
        </p>
        <ul>
          <li>Standard Contractual Clauses (SCCs), or</li>
          <li>Adequacy decisions by the European Commission.</li>
        </ul>
      </div>

      <div class="policy-section">
        <h2>9. Children's Privacy</h2>
        <p>
          Dashboardly is intended for business use and not for individuals under 18. We do not knowingly collect data
          from minors; if such data is identified, it will be deleted.
        </p>
      </div>

      <div class="policy-section">
        <h2>10. Data Breach & Incident Response</h2>
        <ul>
          <li>We maintain a formal incident response plan.</li>
          <li>In case of a breach, we notify affected users within 72 hours (GDPR).</li>
          <li>We cooperate with regulators and take mitigation steps.</li>
        </ul>
      </div>

      <div class="policy-section">
        <h2>11. Complaint Handling</h2>
        <p>If you believe your privacy rights were violated, you may:</p>
        <ul>
          <li>Contact us at hello&#64;dashboardly.io</li>
          <li>Contact our Data Protection Officer (DPO) at hello&#64;dashboardly.io</li>
          <li>File a complaint with your local Data Protection Authority.</li>
        </ul>
      </div>

      <div class="policy-section">
        <h2>12. Updates</h2>
        <p>
          We may update this Privacy Policy periodically. Updates will appear at dashboardly.io/privacy-policy with a
          new "Last Updated" date. Significant changes will also be notified via email or in-app.
        </p>
      </div>

      <div class="policy-section contact-section" id="contact">
        <h2>13. Contact</h2>
        <p>For questions or concerns regarding this Privacy Policy, please contact us at:</p>
        <div class="contact-details">
          <address>
            <div class="company-name">Dashboardly Inc.</div>
            <div class="address-line">800 SE 4TH AVE STE 711</div>
            <div class="address-line">Hallandale Beach, FL 33009</div>
            <div class="contact-line">
              <span>Email:</span>
              <a href="mailto:hello@dashboardly.io">hello&#64;dashboardly.io</a>
            </div>
            <div class="contact-line">
              <span>Phone:</span>
              <a href="tel:+17866363699">+1 (786) 636-3699</a>
            </div>
            <div class="contact-line">
              <span>Website:</span>
              <a href="https://dashboardly.io" target="_blank">https://dashboardly.io</a>
            </div>
          </address>
        </div>
      </div>
    </div>

    <div class="policy-sidebar">
      <div class="sidebar-section">
        <h3>Related policies</h3>
        <ul class="sidebar-links">
          <li><a href="/terms" target="_blank">Terms of Service</a></li>
          <li><a href="/agreement" target="_blank">Seller Agreement</a></li>
        </ul>
      </div>

      <div class="sidebar-section request-data">
        <h3>Request Your Data</h3>
        <p>You can request a copy of your personal data or request deletion at any time.</p>
        <a (click)="scrollToContact($event); $event.preventDefault()" target="_blank" class="request-link">
          Submit a data request
        </a>
      </div>
    </div>
  </div>
</div>
